/**
 * The docked panels' height, in the reader's hands (2026-09-03; owner: "do
 * you think the height should be adjustable for the bottom description
 * section?"). One remembered height for every dock (a part's detail, the
 * infrastructure part's detail): the grip along the top edge drags it, the
 * arrow keys step it, Home and End go to the floor and the ceiling. The
 * drawing above re-fits into whatever is left.
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import { PANEL_MAX_HEIGHT, PANEL_MIN_HEIGHT, useUiStore } from '../../stores/uiStore';

export const PANEL_STEP = 24;

export function usePanelResize(): {
  height: number;
  grip: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
    height: number;
  };
} {
  const stored = useUiStore((s) => s.partPanelHeight);
  const setHeight = useUiStore((s) => s.setPartPanelHeight);
  // While a drag runs the height lives here; the (persisted) store gets one
  // write when it ends, not one per pointer move (2026-09-05).
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const stop = useRef<(() => void) | null>(null);
  const height = dragHeight ?? stored;

  // A panel closed mid-drag (Esc) takes its listeners with it.
  useEffect(() => () => stop.current?.(), []);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      e.preventDefault();
      stop.current?.();
      const startY = e.clientY;
      const startHeight = stored;
      let latest = startHeight;
      const onMove = (ev: globalThis.PointerEvent) => {
        latest = Math.min(PANEL_MAX_HEIGHT, Math.max(PANEL_MIN_HEIGHT, startHeight + (startY - ev.clientY)));
        setDragHeight(latest);
      };
      const end = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        stop.current = null;
        setDragHeight(null);
        setHeight(latest);
      };
      stop.current = end;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', end);
      // A touch the browser claims for scrolling ends in pointercancel, never
      // pointerup: without this the drag would follow every later pointer.
      window.addEventListener('pointercancel', end);
    },
    [stored, setHeight],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'ArrowUp') setHeight(height + PANEL_STEP);
      else if (e.key === 'ArrowDown') setHeight(height - PANEL_STEP);
      else if (e.key === 'Home') setHeight(PANEL_MIN_HEIGHT);
      else if (e.key === 'End') setHeight(PANEL_MAX_HEIGHT);
      else return;
      e.preventDefault();
    },
    [height, setHeight],
  );

  return { height, grip: { onPointerDown, onKeyDown, height } };
}

/** The handle: a short bar on the panel's top edge, keyboard-reachable. */
export function ResizeGrip({
  grip,
  label = 'Resize the panel',
}: {
  grip: { onPointerDown: (e: PointerEvent<HTMLElement>) => void; onKeyDown: (e: KeyboardEvent<HTMLElement>) => void; height: number };
  label?: string;
}) {
  return (
    // A focusable separator is ARIA's window splitter (drag or arrow keys move
    // the edge); jsx-a11y's role table knows only the static kind.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={label}
      aria-valuemin={PANEL_MIN_HEIGHT}
      aria-valuemax={PANEL_MAX_HEIGHT}
      aria-valuenow={grip.height}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the window splitter is reached by Tab, as ARIA asks
      tabIndex={0}
      data-testid="panel-grip"
      title="Drag to resize. Arrow keys step, Home and End go to the ends"
      onPointerDown={grip.onPointerDown}
      onKeyDown={grip.onKeyDown}
      className="group flex h-2.5 shrink-0 touch-none cursor-row-resize items-center justify-center outline-none hover:bg-inset focus-visible:bg-inset"
    >
      <span className="h-0.5 w-8 rounded-pill bg-(--border-strong) transition-colors duration-(--motion-hover) group-hover:bg-(--accent) group-focus-visible:bg-(--accent)" />
    </div>
  );
}
